import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { Button } from '@/components/ui';
import { Download, Plus } from 'lucide-react';
import { useUserList } from './hooks/useUserList';
import { useAuth } from '@/core/auth/AuthContext';
import { useImpersonation } from '@/core/auth/ImpersonationContext';
import { UserStatsCards } from './components/UserStatsCards';
import { UserFilterBar } from './components/UserFilterBar';
import { UserTable } from './components/UserTable';
import { UserQuickDetail } from './components/UserQuickDetail';
import type { UserProfile } from './types/userList.types';

export function UsersListPage() {
  const { profile: authProfile } = useAuth();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  
  const activeProfile = isImpersonating ? impersonatedUser : authProfile;
  const companyId = activeProfile?.company_id || 'f5bdf31a-bd43-439e-b4a5-dabf100cf37d';

  const { users, stats, isLoading, filters, setFilters } = useUserList(companyId);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const handleSearch = (val: string) => {
    setFilters({ ...filters, search: val });
  };

  const handleFilterChange = (key: string, val: string) => {
    setFilters({ ...filters, [key]: val });
  };

  const actions = (
    <>
      <Button variant="outline" className="h-9 gap-2 sm:text-sm text-xs border-slate-200 shadow-sm shrink-0">
        <Download className="w-4 h-4 text-slate-500" />
        <span className="whitespace-nowrap">Xuất danh sách</span>
      </Button>
      <Link to="/users/new">
        <Button variant="primary" className="h-9 gap-2 sm:text-sm text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shrink-0">
          <Plus className="w-4 h-4" />
          <span className="whitespace-nowrap">Thêm nhân sự</span>
        </Button>
      </Link>
    </>
  );

  return (
    <StandardListLayout
      title="Nhân sự"
      description="Quản lý thông tin nhân sự trong hệ thống"
      isLoading={isLoading}
      viewMode="table"
      kpiSection={<UserStatsCards stats={stats} />}
      toolbarSection={
        <UserFilterBar 
          filters={filters} 
          onSearch={handleSearch} 
          onFilterChange={handleFilterChange} 
          actions={actions}
        />
      }
      tableSection={
        <UserTable
          users={users}
          selectedId={selectedUser?.id}
          onSelect={setSelectedUser}
        />
      }
      cardSection={
        <div className="bg-slate-50 border border-slate-200 border-dashed rounded-lg flex flex-col items-center justify-center p-8 text-center text-slate-500 text-sm">
          Giao diện thẻ (Card) chưa được hỗ trợ. Vui lòng xoay ngang màn hình hoặc xem trên Desktop.
        </div>
      }
      sidePanel={
        selectedUser ? (
          <UserQuickDetail
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        ) : undefined
      }
    />
  );
}

export default UsersListPage;
